<script setup lang="ts">
import {
	ref,
	computed,
	onMounted,
	watch,
	nextTick,
	onBeforeUnmount,
} from "vue";
import dayjs from "dayjs";
import { useRouter, useRoute } from "vue-router";
import { useApiClient } from "@/api/client";
import Modal from "@/ui/components/modal.vue";
import useDialog from "@/ui/composables/useDialog";
import CurveSelection from "@/modules/booking_experts/pages/Components/CurveSelection.vue";
import Legend from "@/modules/booking_experts/pages/Components/Legend.vue";
import { toastApiResult } from "@/toaster";
import { between, required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import { useTimeoutPoll } from "@vueuse/core";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import AppliedCurveModal from "@/modules/booking_experts/pages/Components/Modals/AppliedCurveModal.vue";
import {
	colorForFactor,
	colorForAbsoluteOffset,
} from "@/modules/booking_experts/utils";
import { getFlagMessage } from "@/modules/booking_experts/utils/priceUpdateFlags";

import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import CurveApplicationRangePutDTO = Modules.BookingExperts.Data.CurveApplicationRange.CurveApplicationRangePutDTO;
import ComplexPriceItemCurveApplicationStatsDTO = Modules.BookingExperts.Data.MasterPriceList.ComplexPriceItemCurveApplicationStatsDTO;
import { onBeforeRouteLeave } from "vue-router";
import { useBaseStore } from "@/stores/base";
import MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;
import MinMaxOffsetBreachedDate = Modules.BookingExperts.Data.MinMaxOffsetBreachedDate;
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
import RentableTypeAssetDO = Modules.BookingExperts.Data.RentableTypeAssetDO;
import PricelistTypesModal from "@/modules/booking_experts/pages/Components/Modals/PricelistTypesModal.vue";
import { v4 as uuidv4 } from "uuid";

// Types
interface RangeImpact {
	date: Date;
	average_factor?: number | null;
	average_absolute_offset?: number | null;
	length_of_stay: number;
	flags?: number;
}

interface Range {
	id: string;
	start: dayjs.Dayjs;
	end: dayjs.Dayjs;
	color: string;
	curveId?: number;
	impacts?: RangeImpact[];
}

// Constants
const DEFAULT_COLOR = "#19da80";
const DEFAULT_RULES = [
	{ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 },
	{ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 },
];

const INITIAL_PAGE = {
	day: 1,
	month: 1,
	year: dayjs().startOf("year").valueOf(),
};

// Store and API clients
const store = useCurveModelStore();
const router = useRouter();
const route = useRoute();
const baseStore = useBaseStore();
const api = useApiClient("bookingExperts");
const priceId = Number(router.currentRoute.value.params.id);
const dialog = useDialog();
const isBatchAssignment = computed(
	() => router.currentRoute.value.name === "pricing.apply-batch"
);
const applyToAllRanges = ref(false);
const pricelistsToEdit = ref<MasterPriceDO[]>([]);
const currentPriceList = ref<MasterPriceDO | null>(null);

// State - Core data
const stats = ref<ComplexPriceItemCurveApplicationStatsDTO | null>(null);
const curves = ref<PriceModelCurveDO[]>([]);
const ranges = ref<Range[]>([]);
const range = ref<{ start: Date | null; end: Date | null }>({
	start: null,
	end: null,
});
const lengthOfStay = ref(useRoute()?.query?.los ?? 1);
watch(lengthOfStay, () => {
	router.replace({
		path: route.path,
		query: { ...route.query, los: lengthOfStay.value },
	});
});

// State - UI related
const needsToSave = ref(false);
const isFormValid = ref(true);
const loading = ref(false);
const isRefreshing = ref(false);
const isSaving = ref(false);
const isUpdatingPriceList = ref(false);
const showCurveModal = ref(false);
const showAppliedCurveModal = ref(false);
const selectedRange = ref<Range | null>(null);
const selectedCurve = ref<PriceModelCurveDO | null>(null);
const datePickerRef = ref(null);
const datesWhereMinMaxOffsetWasBreached = ref<MinMaxOffsetBreachedDate[]>();
const earliestBreach = ref<Date>(null);
const latestBreach = ref<Date>(null);
const showPricelistTypesModal = ref(false);
const rentableTypes = ref<RentableTypeAssetDO[]>([]);

const rentableTypeDisplayNames = computed(() => {
	if (!rentableTypes.value.length) return "none";

	const MAX_LEN = 5;
	let types = rentableTypes.value
		.map((t) => t.display_name)
		.slice(0, MAX_LEN)
		.join(", ");

	if (rentableTypes.value.length > MAX_LEN) {
		types += ` + ${rentableTypes.value.length - MAX_LEN} more`;
	}
	return types;
});

const isSaveDisabled = computed(
	() =>
		isFormValid.value === false ||
		isRefreshing.value ||
		isSaving.value ||
		showAppliedCurveModal.value ||
		ranges.value.some((r) => !r.curveId)
);

const isFromMinMax = computed(() => route.query.source === "minmax");

const isRefreshDisabled = computed(
	() =>
		isFormValid.value === false ||
		isRefreshing.value ||
		isSaving.value ||
		showAppliedCurveModal.value
);

const isInteractionDisabled = computed(
	() => loading.value || isRefreshing.value || isSaving.value
);

const breachByDate = computed(() => {
	const map = new Map<string, { maxCount: number; minCount: number }>();
	for (const breach of datesWhereMinMaxOffsetWasBreached.value ?? []) {
		const dateKey = dayjs(breach.breachedDate).format("YYYY-MM-DD");
		const existing = map.get(dateKey) ?? { maxCount: 0, minCount: 0 };
		map.set(dateKey, {
			maxCount: existing.maxCount + breach.maxBreachedCount,
			minCount: existing.minCount + breach.minBreachedCount,
		});
	}
	return map;
});

const computedAttributes = computed(() => {
	const result = [];

	// collect dates that have impact warnings so we can center all top dots together.
	const warningDates = new Set<string>();
	for (const curveRange of ranges.value) {
		if (curveRange.impacts?.length) {
			for (const impact of curveRange.impacts) {
				if (impact.flags && impact.flags > 0) {
					warningDates.add(dayjs(impact.date).format("YYYY-MM-DD"));
				}
			}
		}
	}

	const getTopDotPositions = (dateKey: string) => {
		const counts = breachByDate.value.get(dateKey) ?? {
			maxCount: 0,
			minCount: 0,
		};
		const hasMax = counts.maxCount > 0;
		const hasMin = counts.minCount > 0;
		const hasWarning = warningDates.has(dateKey);
		const hasBreaches = hasMax || hasMin;

		if (!hasBreaches)
			return { maxLeft: null, minLeft: null, warningLeft: null };

		const totalDots =
			(hasMax ? 1 : 0) + (hasMin ? 1 : 0) + (hasWarning ? 1 : 0);
		// Each dot is 8px wide with 2px gap so 10 px per dot (except last dot which is 8).
		// that means total width = 10px * totalDots - 2 (remove last gap).
		const totalWidth = 10 * totalDots - 2;
		const centerOffset = totalWidth / 2;
		const positions = Array.from(
			{ length: totalDots },
			(_, i) => `calc(50% - ${centerOffset - i * 10}px)`
		);

		let idx = 0;
		return {
			maxLeft: hasMax ? positions[idx++] : null,
			minLeft: hasMin ? positions[idx++] : null,
			warningLeft: hasWarning ? positions[idx] : null,
		};
	};

	for (const curveRange of ranges.value) {
		result.push({
			key: curveRange.id,
			highlight: {
				fillMode: "solid",
				style: `background-color: ${curveRange.color}; height: 1.3rem; border-radius: 4px;`,
			},
			dates: {
				start: curveRange.start,
				end: curveRange.end,
			},
			popover: { visibility: "hover-focus" },
			customData: {
				range: curveRange,
				id: curveRange.id,
			},
		});

		// Add impact dots for each date if available
		if (curveRange.impacts?.length) {
			for (const impact of curveRange.impacts) {
				const dotColor =
					impact.average_factor != null
						? colorForFactor(impact.average_factor)
						: colorForAbsoluteOffset(impact.average_absolute_offset);
				const hasWarning = impact.flags && impact.flags > 0;
				const dateKey = dayjs(impact.date).format("YYYY-MM-DD");
				const positions = getTopDotPositions(dateKey);

				result.push({
					key: `${curveRange.id}-${dateKey}`,
					dates: impact.date,
					dot: {
						style: `background-color: ${dotColor}; width: 20px; height: 8px; border: 1px solid; border-radius: 3px;`,
					},
					...(hasWarning && {
						bar: {
							style: `background-color: orange; width: 8px; height: 8px; border-radius: 50%; margin-right: 0; position: absolute; ${
								positions.warningLeft
									? `left: ${positions.warningLeft}`
									: "right: 2px"
							}; top: 2px;`,
						},
					}),
					popover: { visibility: "hover-focus" },
					customData: {
						range: curveRange,
						id: curveRange.id,
						stats: {
							average_factor: impact.average_factor,
							average_absolute_offset: impact.average_absolute_offset,
							length_of_stay: impact.length_of_stay,
							flags: impact.flags,
						},
					},
				});
			}
		}
	}

	for (const [dateKey, counts] of breachByDate.value) {
		const positions = getTopDotPositions(dateKey);

		if (counts.maxCount > 0) {
			result.push({
				key: `breach-max-${dateKey}`,
				dates: dayjs(dateKey).toDate(),
				bar: {
					style: `background-color: #2BC626; width: 8px; height: 8px; border-radius: 50%; margin-right: 0; position: absolute; left: ${positions.maxLeft}; top: 2px;`,
				},
				popover: { visibility: "hover-focus" },
				customData: { breach: counts },
				isOffsetBreach: true,
			});
		}
		if (counts.minCount > 0) {
			result.push({
				key: `breach-min-${dateKey}`,
				dates: dayjs(dateKey).toDate(),
				bar: {
					style: `background-color: #D42F2F; width: 8px; height: 8px; border-radius: 50%; margin-right: 0; position: absolute; left: ${positions.minLeft}; top: 2px;`,
				},
				popover: { visibility: "hover-focus" },
				customData: { breach: counts },
				isOffsetBreach: true,
			});
		}
	}

	return result;
});

// This hack is needed because the reactivity of VCalendar sucks.
// We first remove all attributes, then 1 tick later we add all of them back.
// Without this hack, when modifying curves on dates that already have a curve, it won't update the value.
const attributes = ref([]);
watch(
	computedAttributes,
	() => {
		attributes.value = [];
		nextTick(() => {
			attributes.value = computedAttributes.value;
		});
	},
	{ immediate: true }
);

const selectedCurveName = computed(() => {
	return curves.value?.find((c) => c?.id === selectedRange.value?.curveId)
		?.name;
});

// Helper functions

/**
 * Formats a price adjustment factor as a percentage string
 */
function formatRelativePriceAdjustment(factor: number): string {
	const percentage = ((factor - 1) * 100).toFixed(0);
	return percentage.startsWith("-") ? `${percentage}%` : `+${percentage}%`;
}

function formatAbsolutePriceAdjustment(offset: number): string {
	return offset < 0 ? `${offset}€` : `+${offset}€`;
}

function formatImpactStats(
	stats:
		| {
				average_factor?: number | null;
				average_absolute_offset?: number | null;
		  }
		| undefined
): string {
	if (!stats) return "No price adjustment";
	if (stats.average_factor != null) {
		return stats.average_factor === 1.0
			? "No price adjustment"
			: `Price adjustment: ${formatRelativePriceAdjustment(
					stats.average_factor
			  )}`;
	}
	if (stats.average_absolute_offset != null) {
		return stats.average_absolute_offset === 0.0
			? "No price adjustment"
			: `Price adjustment: ${formatAbsolutePriceAdjustment(
					stats.average_absolute_offset
			  )}`;
	}
	return "No price adjustment";
}

function clearSelection() {
	nextTick(() => {
		// Needs to be in nextTick because v-calendar disables the watcher on this input during an update.
		// When applying it nextTick, this guard will be gone from v-calendar and it will pick up the change.
		range.value = {
			start: null,
			end: null,
		};
	});
}

function checkOverlap(
	a1: dayjs.Dayjs,
	b1: dayjs.Dayjs,
	a2: dayjs.Dayjs,
	b2: dayjs.Dayjs
) {
	return (
		(a1.isBefore(b2) && b1.isAfter(a2)) || (a2.isBefore(b1) && b2.isAfter(a1))
	);
}

/**
 * Filters impacts that fall within a specific date range
 */
function filterImpactsByDateRange(
	impacts: RangeImpact[],
	start: dayjs.Dayjs,
	end: dayjs.Dayjs
): RangeImpact[] {
	return impacts.filter((impact) => {
		const impactDate = dayjs(impact.date);
		return (
			(impactDate.isSame(start, "day") || impactDate.isAfter(start)) &&
			(impactDate.isSame(end, "day") || impactDate.isBefore(end))
		);
	});
}

// Range management functions
/**
 * Creates a new date range or splits existing ranges that overlap with the new one
 */
function createOrSplitRange(newStart: Date, newEnd: Date) {
	const start = dayjs(newStart).startOf("day");
	const end = dayjs(newEnd).endOf("day");

	const overlappingRanges = ranges.value.filter((existing) =>
		checkOverlap(start, end, existing.start, existing.end)
	);

	// If no overlaps, simply add the new range
	if (overlappingRanges.length === 0) {
		ranges.value.push({
			id: uuidv4(),
			start,
			end,
			color: DEFAULT_COLOR,
		});
		needsToSave.value = true;
		return;
	}

	// Process overlapping ranges
	const rangesToAdd: Range[] = [];
	const rangesToRemove: string[] = [];

	// Always add the new range
	rangesToAdd.push({
		id: uuidv4(),
		start,
		end,
		color: DEFAULT_COLOR,
		curveId: undefined,
	});

	for (const existing of overlappingRanges) {
		rangesToRemove.push(existing.id);
		const existingImpacts = existing.impacts || [];

		// Handle range before overlap (if any)
		if (start.isAfter(existing.start)) {
			const beforeEnd = start.subtract(1, "day").endOf("day");
			rangesToAdd.push({
				id: existing.id,
				start: existing.start,
				end: beforeEnd,
				color: existing.color,
				curveId: existing.curveId,
				impacts: filterImpactsByDateRange(
					existingImpacts,
					existing.start,
					beforeEnd
				),
			});
		}

		// Handle range after overlap (if any)
		if (end.isBefore(existing.end)) {
			const afterStart = end.add(1, "day").startOf("day");
			rangesToAdd.push({
				id: uuidv4(),
				start: afterStart,
				end: existing.end,
				color: existing.color,
				curveId: existing.curveId,
				impacts: filterImpactsByDateRange(
					existingImpacts,
					afterStart,
					existing.end
				),
			});
		}
	}

	// Update ranges
	ranges.value = ranges.value.filter((r) => !rangesToRemove.includes(r.id));
	ranges.value.push(...rangesToAdd);

	needsToSave.value = true;
	clearSelection();
}

// API functions
/**
 * Fetches curve application ranges for the current price and length of stay
 */
async function fetchRanges() {
	isRefreshing.value = true;
	loading.value = true;

	try {
		const response = await api.getRangesForPrice(priceId, lengthOfStay.value);
		if (response.isOk()) {
			ranges.value = response.value.map((range) => ({
				id: range.id.toString(),
				start: dayjs(range.start_date),
				end: dayjs(range.end_date),
				color: range.color,
				curveId: range.curveId,
				impacts: range.impacts.map((impact) => ({
					date: impact.arrival_date,
					average_factor: impact.average_factor,
					average_absolute_offset: impact.average_absolute_offset,
					length_of_stay: impact.length_of_stay,
					flags: impact.flags,
				})),
			}));
		}
	} catch (error) {
		console.error("Error fetching ranges:", error);
	} finally {
		isRefreshing.value = false;
		loading.value = false;
	}
}

async function fetchMinMaxOffsetBreachedDates() {
	if (isNaN(priceId)) return;
	const response = await api.getMinMaxOffsetBreachedDates(priceId);
	if (response.isOk()) {
		datesWhereMinMaxOffsetWasBreached.value = response.value;
		for (const date of datesWhereMinMaxOffsetWasBreached.value) {
			if (
				!earliestBreach.value ||
				dayjs(date.breachedDate).isBefore(dayjs(earliestBreach.value))
			) {
				earliestBreach.value = dayjs(date.breachedDate).toDate();
			}
			if (
				!latestBreach.value ||
				dayjs(date.breachedDate).isAfter(dayjs(latestBreach.value))
			) {
				latestBreach.value = dayjs(date.breachedDate).toDate();
			}
		}
	}
}
async function fetchCurves() {
	try {
		const response = await api.getCurvesForPriceList(priceId);
		if (response.isOk()) {
			curves.value = response.value;
		}
	} catch (error) {
		console.error("Error fetching curves:", error);
	}
}

async function writeRanges() {
	if (isBatchAssignment.value) {
		if (
			!(await useDialog().confirm({
				title: "Confirm Batch Assignment",
				message: `Are you sure you want to overwrite the curves on ${pricelistsToEdit.value.length} pricelists?`,
			}))
		) {
			return;
		}
	}

	isSaving.value = true;
	loading.value = true;

	try {
		const data: CurveApplicationRangePutDTO[] = ranges.value.map(
			(curveRange) => ({
				id:
					curveRange.id !== undefined && Number(curveRange.id) > 0
						? Number(curveRange.id)
						: undefined,
				curveId: curveRange.curveId,
				from: dayjs(curveRange.start).toDate(),
				to: dayjs(curveRange.end).toDate(),
			})
		);

		if (isBatchAssignment.value) {
			await toastApiResult(
				api.setBatchRanges(pricelistsToEdit.value, { items: data })
			);

			await useDialog().alert({
				title: "Curves Assigned",
				message: "The new curves have been applied to the pricelists.",
			});

			router.push({
				name: "pricing.list",
			});
		} else {
			await toastApiResult(api.setRanges(priceId, { items: data }));
			await Promise.all([fetchCurves(), fetchRanges()]);
		}
	} catch (error) {
		console.error("Error saving ranges:", error);
	} finally {
		isSaving.value = false;
		loading.value = false;
		needsToSave.value = false;
	}
}

async function loadCurveData(date: Date) {
	loading.value = true;
	try {
		const response = await api.getHistoricPriceModelCurve(priceId, {
			"filter[date]": dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
			"filter[los]": lengthOfStay.value,
		});

		if (response.isOk()) {
			stats.value = response.value;
			store.currentCurve = response.value.applied_curve;
			store.referenceData = response.value.ref_data;
			store.currentOccupancy = {
				occupancy: response.value.occupancy,
				bookedOccupancy: response.value.occupancy_at_measure_date,
				leadDay: dayjs(date).diff(dayjs().startOf("day"), "day"),
			};
		}
	} catch (error) {
		console.error("Error fetching curve data:", error);
	} finally {
		loading.value = false;
	}
}

/**
 * Checks if the price list is currently updating prices
 */
async function checkIsUpdatingPrices() {
	try {
		const response = await api.getIsPriceListUpdatingPrices(priceId);
		if (response.isOk()) {
			isUpdatingPriceList.value = response.value.is_updating_prices;
		}
	} catch (error) {
		console.error("Error checking if price list is updating:", error);
	}
}

const { resume: startPollCheckIsUpdatingPrices } = useTimeoutPoll(
	checkIsUpdatingPrices,
	5000
);

// Event handlers
function handleCurveSelect(curve: PriceModelCurveDO) {
	selectedCurve.value = curve;
}

async function applySelectedCurve() {
	if (!selectedRange.value || !selectedCurve.value) return;

	loading.value = true;
	try {
		if (applyToAllRanges.value) {
			unappliedRanges.value.map((r) => {
				selectedRange.value = r;
				selectedRange.value.curveId = selectedCurve.value.id;
				selectedRange.value.color = selectedCurve.value.color;
			});
		} else {
			selectedRange.value.curveId = selectedCurve.value.id;
			selectedRange.value.color = selectedCurve.value.color;
		}
	} catch (error) {
		console.error("Error applying curve:", error);
	} finally {
		loading.value = false;
		showCurveModal.value = false;
		selectedRange.value = null;
		selectedCurve.value = null;
		needsToSave.value = true;
	}
}

function handleCurveSelection(range: Range) {
	selectedRange.value = range;
	showCurveModal.value = true;
}

async function openCurveApplication(range: Range, date: Date) {
	selectedRange.value = range;
	showAppliedCurveModal.value = true;
	await loadCurveData(date);
}

async function deleteRange(id: string) {
	const confirm = await dialog.confirm({
		title: "Delete Range",
		message: "Are you sure you want to delete this range?",
		isDelete: true,
	});

	if (!confirm) return;

	const index = ranges.value.findIndex((r) => r.id === id);
	if (index !== -1) {
		ranges.value.splice(index, 1);
	}

	needsToSave.value = true;
}

/** Navigates to the previous day with impact data */
async function handleDayMinus() {
	if (!stats.value) return;

	loading.value = true;
	try {
		let nearestDate: Date | null = null;
		let nearestRange: Range | null = null;
		const currentDate = dayjs(stats.value.check_in);

		for (const range of ranges.value) {
			if (!range.impacts?.length) continue;

			const previousImpact = range.impacts
				.filter((impact) => dayjs(impact.date).isBefore(currentDate))
				.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())[0];

			if (previousImpact) {
				if (!nearestDate || dayjs(previousImpact.date).isAfter(nearestDate)) {
					nearestDate = previousImpact.date;
					nearestRange = range;
				}
			}
		}

		if (nearestDate && nearestRange) {
			stats.value = undefined;
			selectedRange.value = nearestRange;
			await loadCurveData(nearestDate);
		}
	} catch (error) {
		console.error("Error navigating to previous day:", error);
	} finally {
		loading.value = false;
	}
}

/** Navigates to the next day with impact data */
async function handleDayPlus() {
	if (!stats.value) return;

	loading.value = true;
	try {
		let nearestDate: Date | null = null;
		let nearestRange: Range | null = null;
		const currentDate = dayjs(stats.value.check_in);

		for (const range of ranges.value) {
			if (!range.impacts?.length) continue;

			const nextImpact = range.impacts
				.filter((impact) => dayjs(impact.date).isAfter(currentDate))
				.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())[0];

			if (nextImpact) {
				if (!nearestDate || dayjs(nextImpact.date).isBefore(nearestDate)) {
					nearestDate = nextImpact.date;
					nearestRange = range;
				}
			}
		}

		if (nearestDate && nearestRange) {
			stats.value = undefined; // Show load state
			selectedRange.value = nearestRange;
			await loadCurveData(nearestDate);
		}
	} catch (error) {
		console.error("Error navigating to next day:", error);
	} finally {
		loading.value = false;
	}
}

// Watchers
watch(
	() => range.value,
	async (newValue) => {
		if (isInteractionDisabled.value) return;
		if (!newValue?.start || !newValue?.end) return;

		loading.value = true;
		try {
			createOrSplitRange(newValue.start, newValue.end);
		} catch (error) {
			console.error("Error processing range selection:", error);
		} finally {
			loading.value = false;
			clearSelection();
		}
	}
);

const unappliedRanges = computed(() => {
	return ranges.value.filter((r) => !r.curveId);
});

const legend = computed(() => {
	const steps = Array.from({ length: 11 }, (_, i) => -50 + i * 10);
	const legendObj: Record<string, string> = {};

	for (const pct of steps) {
		const factor = 1 + pct / 100;
		const color = colorForFactor(factor);
		const label = pct === 0 ? "0% (no change)" : `${pct > 0 ? "+" : ""}${pct}%`;
		legendObj[color] = label;
	}

	return legendObj;
});

const managedPricelist = ref<MasterPriceDO | null>(null);

const getRentableTypes = async (priceList: MasterPriceDO) => {
	let rentableTypeIds: number[] = [];
	if (priceList.is_source) {
		const managedPriceList = await api.getMasterPrices({
			"filter[managing_price_list_id]": priceList.id,
		});

		if (managedPriceList.isOk() && managedPriceList.value.length > 0) {
			managedPricelist.value = managedPriceList.value[0];

			rentableTypeIds =
				managedPriceList.value[0].rentable_type_ids_with_cluster;
		}
	} else {
		rentableTypeIds = priceList.rentable_type_ids;
	}

	const response = await api.getRentableTypeDisplayNames(rentableTypeIds);
	if (response.isOk()) {
		rentableTypes.value = response.value;
	}
};

// Lifecycle hooks
const events = new AbortController();
onMounted(async () => {
	await fetchMinMaxOffsetBreachedDates();
	try {
		if (isBatchAssignment.value) {
			store.currentBreadcrumb = "Batch Assign";

			const priceResponses = await Promise.all(
				router.currentRoute.value.query.ids.map((id) => {
					return api.getMasterPrice(id);
				})
			);
			pricelistsToEdit.value = priceResponses
				.map((response) => (response.isOk() ? response.value : null))
				.filter((priceList) => priceList !== null)
				.sort((a: MasterPriceDO, b: MasterPriceDO) =>
					a.name.localeCompare(b.name)
				);
		} else {
			const [_, _1, priceResponse] = await Promise.all([
				fetchCurves(),
				fetchRanges(),
				api.getMasterPrice(priceId),
			]);

			if (priceResponse.isOk()) {
				useBaseStore().SET_ACTIVE_BEX_ADMINISTRATION(
					priceResponse.value.administration_id
				);

				await getRentableTypes(priceResponse.value);

				store.currentBreadcrumb = priceResponse.value.name;
				currentPriceList.value = priceResponse.value;
			}

			startPollCheckIsUpdatingPrices();
		}

		window.addEventListener(
			"keyup",
			(ev) => {
				if (ev.key === "Escape") {
					clearSelection();
				}
			},
			{ signal: events.signal }
		);
	} catch (error) {
		console.error("Error during component initialization:", error);
	}
});

onBeforeUnmount(() => {
	events.abort();
});

onBeforeRouteLeave((to, from, next) => {
	unsavedChangesDialog("pricelist", needsToSave.value, next, () => {
		store.currentBreadcrumb = null;
	});
});

const visibleMonths = ref<{ month: number; year: number }[]>([]);

const onPageUpdate = (pages: { month: number; year: number }[]) => {
	visibleMonths.value = pages.map(({ month, year }) => ({ month, year }));
};

const visibleRangesCurveIds = computed(() => {
	const firstMonth = visibleMonths.value[0];
	const lastMonth = visibleMonths.value[visibleMonths.value.length - 1];

	if (!firstMonth || !lastMonth) {
		return ranges.value;
	}

	// vc-date-picker months are 1-based, dayjs months are 0-based
	const windowStart = dayjs()
		.year(firstMonth.year)
		.month(firstMonth.month - 1)
		.startOf("month");
	const windowEnd = dayjs()
		.year(lastMonth.year)
		.month(lastMonth.month - 1)
		.endOf("month");

	return ranges.value
		.filter(
			(range) =>
				!range.start.isAfter(windowEnd) && !range.end.isBefore(windowStart)
		)
		.map((range) => range.curveId);
});

const visibleCurves = computed(() =>
	curves.value.filter((curve) =>
		visibleRangesCurveIds.value.includes(curve.id as number)
	)
);

const yesterday = computed(() => dayjs().subtract(1, "day").endOf("day"));
const defaultMaxEndDate = computed(() =>
	dayjs().startOf("year").add(3, "years")
);

const minStartDate = computed(() =>
	ranges.value
		.reduce(
			(earliest, range) =>
				range.start.isBefore(earliest) ? range.start : earliest,
			yesterday.value
		)
		.toDate()
);

const maxEndDate = computed(() =>
	ranges.value
		.reduce(
			(latest, range) => (range.end.isAfter(latest) ? range.end : latest),
			defaultMaxEndDate.value
		)
		.toDate()
);

const disabledPastDates = [{ start: null, end: yesterday.value.toDate() }];
</script>

<template>
	<!-- Modals -->
	<Modal
		v-model:visible="showCurveModal"
		title="Select Curve"
		icon="fa-solid fa-chart-line"
	>
		<CurveSelection
			:selected-curve-id="selectedRange?.curveId"
			:allowed-to-edit="selectedRange?.end.isAfter(new Date())"
			@select="handleCurveSelect"
		/>
		<v-checkbox
			v-if="unappliedRanges.length > 1"
			v-model="applyToAllRanges"
			label="Apply to all unapplied ranges"
			hide-details
		/>
		<template #actions>
			<v-btn
				color="primary"
				:disabled="!selectedCurve"
				@click="applySelectedCurve"
			>
				Apply
			</v-btn>
		</template>
	</Modal>

	<AppliedCurveModal
		v-model="stats"
		:curve-data="{
			id: selectedRange?.curveId?.toString(),
			name: selectedCurveName,
		}"
		v-model:visible="showAppliedCurveModal"
		@day:minus="handleDayMinus"
		@day:plus="handleDayPlus"
	/>

	<PricelistTypesModal
		v-if="showPricelistTypesModal"
		v-model="showPricelistTypesModal"
		:pricelist-name="store.currentBreadcrumb"
		:rentable-type-assets="rentableTypes"
	/>

	<div class="booking-calendar-container mt-5">
		<!-- Header Section -->
		<v-card class="mb-4 pa-4" v-if="isBatchAssignment">
			<div class="d-flex flex-column">
				<h2 class="mb-2">Batch Assignment</h2>
				<p class="text-body-2">
					Modifying
					{{ pricelistsToEdit.map((curve) => `"${curve.name}"`).join(", ") }}
				</p>
			</div>
			<v-divider class="my-3"></v-divider>
			<div class="d-flex">
				<div class="d-flex align-center gap-2">
					<v-icon icon="mdi-information-outline" color="info" size="small" />
					<div>
						<p class="text-caption text-medium-emphasis">
							Create ranges by clicking and dragging on the calendar.
						</p>
						<p class="text-caption text-medium-emphasis">
							The price computation takes time, you may not see an immediate
							change in the pricing factors.
						</p>
					</div>
				</div>
				<v-spacer />
				<v-badge dot color="red" v-model="needsToSave">
					<v-btn
						@click="writeRanges"
						:loading="isSaving"
						:disabled="isSaveDisabled"
						variant="outlined"
						class="border"
						prepend-icon="fa-regular fa-save"
						data-testid="save-yearedit-overview"
					>
						Save
					</v-btn>
				</v-badge>
			</div>
		</v-card>
		<v-card class="mb-4 pa-4" v-else>
			<div class="d-flex justify-space-between align-center">
				<div class="d-flex flex-column">
					<div class="d-flex gap-2">
						<v-btn
							v-tooltip="'View in Booking Experts'"
							:href="`https://app.bookingexperts.com/parks/${baseStore.selectedBexAdministration?.id}/master_price_lists/${priceId}/rentable_types`"
							color="success"
							variant="tonal"
							size="36"
							target="_blank"
							rel="noopener noreferrer"
							prepend-icon="fa-solid fa-cloud"
						/>
						<h2 class="mb-2">Booking Calendar</h2>
					</div>
					<p class="text-body-2 text-medium-emphasis">
						You are able to see an overview of applied factors for each day
						within a selected model range.
					</p>
					<div v-if="currentPriceList" class="d-flex align-center gap-2 mt-2">
						<p class="text-body-2 text-medium-emphasis">
							Capacity:
							<strong>{{
								(managedPricelist?.capacity_max ?? 0) +
									currentPriceList.capacity_max || "-"
							}}</strong>
						</p>
					</div>
				</div>
				<div
					v-if="isUpdatingPriceList"
					class="text-medium-emphasis text-sm-caption d-flex align-center"
				>
					<p class="mr-2">Prices are being updated</p>
					<v-progress-circular color="primary" indeterminate class="mr-2" />
				</div>
			</div>

			<div class="d-flex align-center">
				<p class="text-body-2 text-medium-emphasis mr-2">
					Rentable Types:
					{{ rentableTypeDisplayNames }}
				</p>

				<v-btn
					v-if="rentableTypes.length > 4"
					size="12"
					variant="text"
					v-tooltip="t('pricelist.curves.table.actions.showUsedRentableTypes')"
					@click="showPricelistTypesModal = true"
				>
					<i class="fa-solid fa-circle-info"></i>
				</v-btn>
			</div>

			<v-divider class="my-3"></v-divider>

			<!-- Controls area -->
			<div class="d-flex align-center flex-wrap gap-1">
				<v-form v-model="isFormValid" class="d-flex align-center gap-2">
					<v-text-field
						v-model.number="lengthOfStay"
						density="compact"
						variant="outlined"
						width="150"
						label="Length of Stay"
						hide-details
						type="number"
						step="1"
						:rules="[required(t), between(t, 0, 10)]"
						min="1"
						max="30"
					/>

					<v-btn
						@click="fetchRanges"
						:disabled="isRefreshDisabled"
						prepend-icon="mdi-refresh"
						color="secondary"
						variant="flat"
						height="40"
					>
						Refresh
					</v-btn>
				</v-form>

				<v-divider vertical class="mx-2"></v-divider>

				<div class="d-flex align-center gap-2">
					<v-icon icon="mdi-information-outline" color="info" size="small" />
					<div>
						<p class="text-caption text-medium-emphasis">
							Create ranges by clicking and dragging on the calendar.
						</p>
						<p class="text-caption text-medium-emphasis">
							The price computation takes time, you may not see an immediate
							change in the pricing factors.
						</p>
					</div>
				</div>
				<v-spacer />
				<v-badge dot color="red" v-model="needsToSave">
					<v-btn
						@click="writeRanges"
						:loading="isSaving"
						:disabled="isSaveDisabled"
						variant="outlined"
						class="border"
						prepend-icon="fa-regular fa-save"
						data-testid="save-yearedit-overview"
					>
						Save
					</v-btn>
				</v-badge>
			</div>
		</v-card>

		<!-- Main Calendar Area -->
		<v-card :ref="datePickerRef" class="mb-4" :loading="loading" elevation="0">
			<div class="calendar-container pa-4">
				<vc-date-picker
					:first-day-of-week="2"
					:rows="3"
					:columns="4"
					:step="1"
					borderless
					show-iso-weeknumbers
					:disable-page-swipe="true"
					v-model.range="range"
					mode="date"
					:rules="DEFAULT_RULES"
					:attributes="attributes"
					:disabled="loading"
					:min-date="minStartDate"
					:max-date="maxEndDate"
					:disabled-dates="disabledPastDates"
					:initial-page="{
						month: new Date().getMonth() + 1,
						year: new Date().getFullYear(),
					}"
					:show-adjacent-months="false"
					@update:pages="onPageUpdate"
					@dayclick="
						(day, event) => {
							event.target.blur();
						}
					"
					@drag="
						(e) => {
							if (e.dragValue === null) clearSelection();
						}
					"
					style="background-color: rgb(var(--color-surface))"
				>
					<template #day-popover="{ attributes, hide }">
						<template v-if="attributes.some((attr) => attr.highlight)">
							<div class="d-flex gap-2">
								<v-btn
									v-if="
										attributes.some((attr) => attr.dot && !attr.isOffsetBreach)
									"
									@click="() => {
                    hide();
                    const currentRange = attributes.find((attr) => !attr.isOffsetBreach).customData.range as Range;
                    const on = attributes.find(attr => attr.dot)?.dates;
                    openCurveApplication(currentRange, on);
                  }"
									size="32"
									variant="text"
									:disabled="isInteractionDisabled"
								>
									<v-icon icon="fa-solid fa-eye" />
								</v-btn>
								<v-btn
									@click="
										() => {
											hide();
											handleCurveSelection(
												attributes.find((attr) => !attr.isOffsetBreach)
													.customData.range
											);
										}
									"
									size="32"
									variant="text"
									color="primary"
									:disabled="isInteractionDisabled"
									data-testid="assign-curve"
								>
									<v-icon icon="mdi-chart-line" />
								</v-btn>
								<v-btn
									@click="
										async () => {
											hide();
											await deleteRange(
												attributes.find((attr) => !attr.isOffsetBreach)
													.customData.id
											);
										}
									"
									color="error"
									size="32"
									variant="text"
									:disabled="isInteractionDisabled"
									data-testid="delete-range"
								>
									<v-icon icon="mdi-delete" />
								</v-btn>
							</div>
						</template>

						<!-- Stats info -->
						<div class="d-flex flex-column gap-1">
							<template
								v-if="attributes.some((attr) => attr.customData?.stats)"
							>
								<v-divider />
								<p
									class="text-caption"
									:class="{
										'text-success':
											(attributes.find((attr) => attr.customData?.stats)
												?.customData?.stats?.average_factor ?? 1) > 1 ||
											(attributes.find((attr) => attr.customData?.stats)
												?.customData?.stats?.average_absolute_offset ?? 0) > 0,
										'text-error':
											(attributes.find((attr) => attr.customData?.stats)
												?.customData?.stats?.average_factor ?? 1) < 1 ||
											(attributes.find((attr) => attr.customData?.stats)
												?.customData?.stats?.average_absolute_offset ?? 0) < 0,
									}"
								>
									{{
										formatImpactStats(
											attributes.find((attr) => attr.customData?.stats)
												?.customData?.stats
										)
									}}
								</p>
								<!-- Warning indicators for special price flags -->
								<p
									v-for="(msg, index) in getFlagMessage(
										attributes.find((attr) => attr.customData?.stats)
											?.customData?.stats?.flags
									)"
									:key="index"
									class="text-caption text-warning d-flex align-center"
								>
									<v-icon
										icon="fa-solid fa-triangle-exclamation"
										size="x-small"
										class="mr-1"
										color="warning"
									/>
									{{ msg }}
								</p>
							</template>

							<!-- Min/Max offset breach indicators -->
							<template
								v-if="attributes.some((attr) => attr.customData?.breach)"
							>
								<v-divider />
								<p class="text-caption font-weight-medium">Offset breaches:</p>
								<p
									v-if="
										attributes.find((attr) => attr.customData?.breach)
											?.customData?.breach?.maxCount > 0
									"
									class="text-caption d-flex align-center gap-1"
									style="color: #2bc626"
								>
									<v-icon icon="fa-solid fa-arrow-up" size="x-small" />
									Max offset hit:
									{{
										attributes.find((attr) => attr.customData?.breach)
											?.customData?.breach?.maxCount
									}}x
								</p>
								<p
									v-if="
										attributes.find((attr) => attr.customData?.breach)
											?.customData?.breach?.minCount > 0
									"
									class="text-caption d-flex align-center gap-1"
									style="color: #d42f2f"
								>
									<v-icon icon="fa-solid fa-arrow-down" size="x-small" />
									Min offset hit:
									{{
										attributes.find((attr) => attr.customData?.breach)
											?.customData?.breach?.minCount
									}}x
								</p>
							</template>
						</div>
					</template>
				</vc-date-picker>

				<!-- Color Legend -->
				<v-card v-if="!isBatchAssignment" flat class="mt-4 pa-2" elevation="0">
					<div class="d-flex flex-wrap align-center justify-center gap-4">
						<div
							class="d-flex align-center"
							v-for="(value, key) in legend"
							:key="key"
						>
							<div
								class="color-sample mr-2"
								:style="{ backgroundColor: key }"
							></div>
							<span class="text-caption">{{ value }}</span>
						</div>
					</div>
				</v-card>
			</div>
		</v-card>

		<!-- Price List Legend -->
		<Legend v-if="!isBatchAssignment" :curves="visibleCurves" />
	</div>
</template>

<style scoped lang="scss">
.booking-calendar-container {
	max-width: 1200px;
	margin: 0 auto;
}

.calendar-container {
	width: 100%;
}

.gap-4 {
	gap: 16px;
}

.gap-2 {
	gap: 8px;
}

.gap-1 {
	gap: 4px;
}

.color-sample {
	width: 16px;
	height: 8px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 3px;
}

:deep(.vc-container) {
	--day-content-transition-time: 0.13s;
	border: none;
	width: 100%;
}

:deep(.vc-weeks) {
	padding: 0;
}

/* Improve the appearance of dots */
:deep(.vc-dot) {
	transition: all 0.3s ease;
	margin-bottom: 2px;
	position: relative;
	z-index: 25 !important; /* Keep dots above everything */
}

/* Ensure the drag selection overlay is on top */
:deep(.vc-day.vc-in-drag) {
	position: relative;
	z-index: 30 !important;
}

/* Make blue selection more visible with a semi-transparent overlay */
:deep(.vc-in-range:not(.vc-highlights *)) {
	position: relative;

	&::after {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(33, 150, 243, 0.25);
		pointer-events: none;
		z-index: 25;
	}
}

:deep(.vc-range-start:not(.vc-highlights *)),
:deep(.vc-range-end:not(.vc-highlights *)) {
	position: relative;

	&::after {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(33, 150, 243, 0.4);
		pointer-events: none;
		z-index: 25;
		border-radius: 50%;
	}
}

.vc-highlight.vc-highlight-bg-outline {
	z-index: 100;
}
</style>
